import split from "lodash/split";
import React, {
  ChangeEvent,
  CSSProperties,
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

interface Props {
  className?: string;
  onChange: (newText: string) => void;
  placeholder: string;
  style?: CSSProperties;
  value: string;
  focus?: boolean;
}

const AdvancedTextArea: FunctionComponent<Props> = ({
  className,
  onChange,
  placeholder,
  style = {},
  value,
  focus,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [selection, setSelection] = useState<{ end: number; start: number }>({
    end: value.length,
    start: value.length,
  });
  const [changedLength, setChangedLength] = useState<number>(0);
  const [newLineInserted, setNewLineInserted] = useState<boolean>(false);
  const [textCanceled, setTextCanceled] = useState<boolean>(false);
  const [oldValue, setOldValue] = useState<string>(
    textareaRef.current ? textareaRef.current.value : ""
  );

  useEffect(() => {
    const element = textareaRef.current;
    if (!element) {
      return;
    }

    if (textCanceled) {
      element.selectionStart = selection.start - changedLength;
      element.selectionEnd = selection.end - changedLength;
      setTextCanceled(false);
    } else if (changedLength < 0 || (newLineInserted && selection.start > 0)) {
      element.selectionStart = selection.start;
      element.selectionEnd = selection.end;
      setNewLineInserted(false);
    }
    if (element && focus) {
      element.focus();
    }
  }, [focus, changedLength, newLineInserted, selection, textCanceled]);

  useEffect(() => {
    const element = textareaRef.current;
    if (!element) {
      return;
    }

    if (element && focus) {
      element.focus();
    }
  }, [className, focus]);

  const onFocus = useCallback(() => {
    const element = textareaRef.current;
    if (element) {
      element.selectionStart = selection.start;
      element.selectionEnd = selection.end;
    }
  }, [selection]);

  const setSelectionFromEvent = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setSelection({
      end: event.target.selectionEnd,
      start: event.target.selectionStart,
    });
  };

  const valueForRows = value ? value : placeholder;
  let rows = split(valueForRows, "\n").length;

  if (
    textareaRef.current &&
    newLineInserted === false &&
    rows > split(textareaRef.current.value, "\n").length
  ) {
    setNewLineInserted(true);
  }

  return (
    <textarea
      ref={textareaRef}
      className={className}
      onChange={(event) => {
        const newText = event.target.value;
        setSelectionFromEvent(event);
        setChangedLength(newText.length - value.length);
        if (newText !== value && oldValue !== "" && value === oldValue) {
          setTextCanceled(true);
        }
        rows = split(newText, "\n").length;

        onChange(newText);
        setOldValue(value);
      }}
      onFocus={onFocus}
      placeholder={placeholder}
      rows={rows}
      style={style}
      value={value}
    />
  );
};

export default AdvancedTextArea;
